/**
 * Helper para gerenciar invite_id recebido via link de email.
 *
 * Quando o usuário clica no link do email de convite, a URL contém
 * o parâmetro ?invite_id=<uuid>. Esse valor é salvo no localStorage
 * para sobreviver a redirects de autenticação.
 *
 * Após o login, o sistema detecta o valor e abre automaticamente
 * o dialog de convites. Ao aceitar ou rejeitar, o valor é removido.
 */

const INVITE_LINK_STORAGE_KEY = "snap-reports:pending-invite-ids";

/**
 * Captura o invite_id da URL e salva no localStorage.
 * Deve ser chamado o mais cedo possível no ciclo de vida do app.
 */
export function captureInviteIdFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const inviteId = params.get("invite_id");

    if (!inviteId) return;

    const existing = getPendingInviteIds();
    if (!existing.includes(inviteId)) {
      existing.push(inviteId);
      localStorage.setItem(INVITE_LINK_STORAGE_KEY, JSON.stringify(existing));
    }

    // Limpa o parâmetro da URL sem recarregar a página
    params.delete("invite_id");
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
    window.history.replaceState({}, "", newUrl);
  } catch (error) {
    console.warn("[InviteLink] Falha ao capturar invite_id da URL:", error);
  }
}

/**
 * Retorna a lista de invite_ids pendentes no localStorage.
 */
export function getPendingInviteIds(): string[] {
  try {
    const raw = localStorage.getItem(INVITE_LINK_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Verifica se há invite_ids pendentes.
 */
export function hasPendingInviteIds(): boolean {
  return getPendingInviteIds().length > 0;
}

/**
 * Remove um invite_id específico da lista (após aceitar ou rejeitar).
 */
export function removePendingInviteId(inviteId: string): void {
  try {
    const current = getPendingInviteIds();
    const updated = current.filter((id) => id !== inviteId);
    if (updated.length > 0) {
      localStorage.setItem(INVITE_LINK_STORAGE_KEY, JSON.stringify(updated));
    } else {
      localStorage.removeItem(INVITE_LINK_STORAGE_KEY);
    }
  } catch (error) {
    console.warn("[InviteLink] Falha ao remover invite_id:", error);
  }
}

/**
 * Remove todos os invite_ids pendentes.
 */
export function clearAllPendingInviteIds(): void {
  try {
    localStorage.removeItem(INVITE_LINK_STORAGE_KEY);
  } catch (error) {
    console.warn("[InviteLink] Falha ao limpar invite_ids:", error);
  }
}

/**
 * Retorna o valor salvo no localStorage para preservar durante localStorage.clear().
 * Deve ser chamado ANTES do clear, e o valor restaurado DEPOIS.
 */
export function backupPendingInviteIds(): string | null {
  try {
    return localStorage.getItem(INVITE_LINK_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Restaura o valor salvo após um localStorage.clear().
 */
export function restorePendingInviteIds(backup: string | null): void {
  try {
    if (backup) {
      localStorage.setItem(INVITE_LINK_STORAGE_KEY, backup);
    }
  } catch (error) {
    console.warn("[InviteLink] Falha ao restaurar invite_ids:", error);
  }
}
